import { inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { AuthService } from '../services/auth.service';

/**
 * Attaches the bearer token to API calls and owns the 401 path.
 *
 * Two rules keep it from misfiring:
 *
 * 1. It only touches requests aimed at the API. Anything else — SVG icons
 *    fetched by `MatIconRegistry`, i18n files, other static assets — passes
 *    straight through. `HttpClient` is used for those too, and treating them as
 *    API traffic used to sign the user out on any public page.
 * 2. A missing token is not, by itself, a reason to sign out. Only a real 401
 *    from the API triggers refresh-then-sign-out.
 */
export function authInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!isApiRequest(req.url) || isAuthEndpoint(req.url)) {
    return next(req);
  }

  return from(authService.getBearer()).pipe(
    catchError(() => from([null])),
    switchMap(bearerToken => {
      const outgoing = bearerToken
        ? addTokenToRequest(req, bearerToken.Authorization.replace('Bearer ', ''))
        : req;

      return next(outgoing).pipe(
        catchError((err: HttpErrorResponse) => {
          if (err.status !== 401) {
            return throwError(() => err);
          }

          // Expired between the token check and the request, or never valid:
          // one refresh attempt, then give up and clear the session.
          return from(authService.refreshAccessToken()).pipe(
            switchMap(newAccessToken => {
              if (newAccessToken) {
                return next(addTokenToRequest(req, newAccessToken));
              }
              authService.signOut('auth.interceptor.ts - 401 and refresh failed', router.url);
              return throwError(() => err);
            }),
            catchError(refreshError => {
              authService.signOut('auth.interceptor.ts - refresh threw', router.url);
              return throwError(() => refreshError ?? err);
            })
          );
        })
      );
    })
  );
}

/**
 * True when the request targets the backend. Assets are served from the app's
 * own origin as relative paths, so anything not pointed at the API is skipped.
 */
function isApiRequest(url: string): boolean {
  return (
    url.startsWith(environment.apiBaseUrl) ||
    url.startsWith(`${environment.host}:${environment.port}`)
  );
}

/** Endpoints that must never carry (or be blocked by) a bearer token. */
function isAuthEndpoint(url: string): boolean {
  if (url.includes('/subscribe_notification')) {
    return false; // requires auth despite living under /authentication
  }
  return [
    '/authentication/sign_in',
    '/authentication/sign_up',
    '/authentication/validation_email',
    '/authentication/refresh_token',
    '/authentication/request_password_reset',
    '/authentication/reset_password',
    '/authentication/validate_set_password_token',
    '/authentication/set_password_with_token',
    '/authentication/google_sign_in',
    '/authentication/google_oauth_callback',
  ].some(endpoint => url.includes(endpoint));
}

function addTokenToRequest(req: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
  if (!token || req.headers.has('Authorization')) {
    return req;
  }
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}
